import { useEffect, useState } from 'react';
import { format } from 'date-fns';
import { Clock, Pause, Play } from 'lucide-react';

import { Button } from 'components/ui/button';
import { useUploadVoiceRecording } from 'services/voice-scan/hooks';
import { captureUserActivity } from 'services/profile/api';
import { USER_ACTIVITY } from 'types';
import { useVoiceScanStore } from 'store/voice-scan';
import { useMediaPredicate } from 'react-media-hook';
import { useTranslation } from 'react-i18next';

const formatDuration = (seconds: number, duration: number) => {
  const minutes = Math.floor(Math.ceil(seconds) / 60);
  const remainingSeconds = duration - Math.round(seconds);
  return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
};

function formatDate(date: Date) {
  return format(date, "MMMM d, yyyy 'at' h:mm a");
}
function formatMobileDate(date: Date) {
  return format(date, 'dd/MM HH:mm');
}

export default function AudioReview({
  onSubmit,
  onRetry,
}: {
  onSubmit: () => void;
  onRetry: () => void;
}) {
  const { t } = useTranslation();
  const isMediaMobile = useMediaPredicate('(max-width: 640px)');

  const {
    recordingsRef,
    imageData,
    recordedWavesurfer,
    audioMetaData,
    recordingStartTime,
    sessionId,
    duration,
  } = useVoiceScanStore();

  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);

  const { mutate: uploadRecording } = useUploadVoiceRecording({
    onMutate: () => {
      captureUserActivity(USER_ACTIVITY.VOICE_SCAN_UPLOAD_START, {
        session_id: sessionId ?? '',
      });
    },
    onSuccess: () => {
      captureUserActivity(USER_ACTIVITY.VOICE_SCAN_UPLOAD_COMPLETE, {
        session_id: sessionId ?? '',
      });
    },
    onError: (error) => {
      captureUserActivity(USER_ACTIVITY.VOICE_SCAN_UPLOAD_ERROR, {
        error: error?.message || 'Unknown error',
        session_id: sessionId ?? '',
      });
    },
  });

  useEffect(() => {
    if (!recordedWavesurfer) return;

    const unsubscribe = recordedWavesurfer.on('timeupdate', (currentTime) => {
      setCurrentTime(currentTime);
    });

    recordedWavesurfer.on('finish', () => {
      setIsPlaying(false);
    });

    return () => {
      unsubscribe();
    };
  }, [recordedWavesurfer]);

  useEffect(() => {
    if (audioMetaData && recordedWavesurfer) {
      // const duration = Math.ceil(recordedWavesurfer.getDuration());

      captureUserActivity(USER_ACTIVITY.VOICE_SCAN_RECORDING_STOP, {
        session_id: sessionId ?? '',
      });
      uploadRecording({
        audioBlob: audioMetaData?.wav,
        sessionId: sessionId ?? '',
        duration: duration,
        format: 'wav',
      });
    }
  }, [audioMetaData, recordedWavesurfer]);

  const onPlayPause = () => {
    if (!recordedWavesurfer) {
      return;
    }

    recordedWavesurfer.playPause();
    setIsPlaying((prev) => !prev);
  };

  return (
    <div className='min-h-[calc(100vh-7rem)] mx-2 lg:mx-6 py-6 px-4 grid place-items-center'>
      <div className='w-full'>
        <div className='space-y-2 text-center'>
          <h1 className='text-[#0E2439] font-semibold text-xl'>
            {t('recording_complete')} ✅
          </h1>
          <p className='text-[#656D76] font-medium'>{t('audio_review_desc')}</p>
        </div>

        <div className='grid grid-cols-1 gap-8 mt-8 lg:mt-16 lg:px-4 lg:grid-cols-2 lg:gap-20'>
          <div className='flex gap-4'>
            <div className='h-32 aspect-square rounded-3xl'>
              <img
                crossOrigin='anonymous'
                src={imageData?.image_url}
                alt={imageData?.image_id}
                className='object-contain w-full h-full rounded-3xl'
              />
            </div>
            <div className='flex-1'>
              <h2 className='text-lg font-semibold lg:text-xl text-tertiary'>
                {t('recording_details')}
              </h2>
              <div className='mt-2 space-y-2'>
                <div className='flex items-center justify-between'>
                  <p className='font-semibold'>{t('duration')}:</p>
                  <p>
                    {duration} {t('seconds')}
                  </p>
                </div>

                <div className='flex items-center justify-between'>
                  <p className='font-semibold'>{t('audio_file_size')}:</p>
                  <p>{audioMetaData?.fileSizeInMB} MB</p>
                </div>

                <div className='flex items-center justify-between'>
                  <p className='font-semibold'>{t('recorded')}:</p>
                  <p>
                    {recordingStartTime
                      ? isMediaMobile
                        ? formatMobileDate(recordingStartTime)
                        : formatDate(recordingStartTime)
                      : '-'}
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div className='w-full bg-[#E8F0FB] flex-1 rounded-3xl p-4 flex-col'>
            <p className='font-medium text-center'>
              {t('audio_playback_message')}
            </p>
            <div className='flex items-center flex-1 w-full h-12 gap-2 p-4 mt-2 bg-white md:h-16 rounded-3xl'>
              <button
                className='hidden md:grid bg-[#E8F0FB] rounded-full h-10 w-10 place-content-center'
                onClick={onPlayPause}
              >
                {isPlaying ? (
                  <Pause fill='#3177DC' size={20} color='#3177DC' />
                ) : (
                  <Play fill='#3177DC' size={20} color='#3177DC' />
                )}
              </button>
              <div className='hidden md:flex items-center w-[70px] justify-center gap-1 px-2 py-1 border rounded-lg border-tertiary'>
                <Clock size={16} color='#3177DC' />
                <p className='text-sm font-medium text-tertiary pt-0.5'>
                  {formatDuration(currentTime, duration)}
                </p>
              </div>
              <div
                id='recordings'
                ref={recordingsRef}
                className='flex-1 w-full'
              />
            </div>
            <div className='flex justify-center md:hidden'>
              <button
                className='flex items-center justify-center gap-1.5 border border-[#3177DC] px-4 mt-4 h-9 rounded-2xl font-medium bg-[#3177DC] text-white'
                onClick={onPlayPause}
              >
                {isPlaying ? (
                  <>
                    <Pause fill='#fff' size={18} color='#fff' />
                    <span>{t('pause')}</span>
                  </>
                ) : (
                  <>
                    <Play fill='#fff' size={18} color='#fff' />
                    <span>{t('play')}</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        <div className='flex items-center justify-center gap-4 mt-20'>
          <Button
            variant='outline'
            className='border-[#0E2439] px-8'
            size='sm'
            onClick={onRetry}
          >
            <span>{t('retry')}</span>
          </Button>
          <Button size='sm' className='px-8' onClick={onSubmit}>
            <span>{t('submit')}</span>
          </Button>
        </div>
      </div>
    </div>
  );
}
